/**
 * The console's staff-identity route gate — task 31.
 *
 * The staff-identity axis is deliberately separate from the five-tier client
 * confidence model (communicatorv2.3.md §5: "Staff auth is entirely separate
 * from client identity... The five-tier confidence model does not apply to
 * staff; it was only ever about verifying clients"). This file is the only
 * place in the console that reasons about whether *staff* is signed in — it
 * shares no code, copy, or vocabulary with `assertGate`'s tiers or any
 * client-verification badge; conflating the two would be exactly the
 * category error the design doc calls out.
 *
 * `store.authStatus` (src/store.ts) starts `'checking'` at boot and is set
 * for real once `src/bootstrap.ts`'s `identity.whoAmI()` round trip resolves,
 * or by a sign-in/sign-out action afterward (src/pages/SignInPage.tsx,
 * src/components/shell/InitialsMenu.tsx). Both guards below react live to
 * that value changing while mounted — neither `SignInPage` nor
 * `InitialsMenu` calls `navigate()` itself; flipping `authStatus` is enough,
 * because whichever guard is currently mounted (this file is the only thing
 * that renders `<Navigate>`) picks up the change and redirects on its own.
 */
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { remember, take } from '../../lib/intendedDestination'
import { useStore } from '../../store'

/**
 * `'checking'` renders a minimal placeholder rather than redirecting, so a
 * signed-in visitor never sees a sign-in flash while the first
 * `GET /ops/whoami` is still in flight. `'signed-out'` redirects to
 * `/sign-in`. `'signed-in'` renders the protected page.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const authStatus = useStore((s) => s.authStatus)
  const location = useLocation()

  if (authStatus === 'checking') {
    return (
      <div
        data-testid="auth-checking"
        className="flex min-h-screen items-center justify-center bg-page text-[13px] text-mid"
      >
        Checking your session…
      </div>
    )
  }
  if (authStatus === 'signed-out') {
    // Remember what they clicked. Halo's deep links land here constantly —
    // a tech opens a ticket, clicks through to the conversation, and is asked
    // to sign in first; without this the thread they were going to is lost
    // and they have to hunt for it.
    remember(`${location.pathname}${location.search}`)
    return <Navigate to="/sign-in" replace />
  }
  return <>{children}</>
}

/** The inverse guard on /sign-in itself: an already-signed-in visitor is sent straight through to the console. */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const authStatus = useStore((s) => s.authStatus)
  if (authStatus === 'signed-in') {
    // Back where they were going, if we know — otherwise the front page,
    // which is the behaviour this had before deep links worked at all. Both
    // sign-in routes end up here: the code flow lands on /sign-in and this
    // guard bounces it, and the Entra flow returns to /sign-in for the same
    // reason, so neither needs its own copy of this.
    return <Navigate to={take() ?? '/'} replace />
  }
  return <>{children}</>
}
